"use client";

import { useQuery } from "@tanstack/react-query";

async function fetchOiBulk(cids: string[]): Promise<Map<string, number>> {
  if (cids.length === 0) return new Map();
  const qs = new URLSearchParams({ cids: cids.join(",") });
  const res = await fetch(`/api/analytics/markets/oi-bulk?${qs.toString()}`);
  if (!res.ok) return new Map();
  const json = await res.json();
  const data = (json?.data || {}) as Record<string, number>;
  const map = new Map<string, number>();
  for (const [k, v] of Object.entries(data)) {
    if (typeof v === "number" && Number.isFinite(v)) {
      map.set(k.toLowerCase(), v);
    }
  }
  return map;
}

/**
 * Bulk Open Interest lookup — fetches OI for all provided conditionIds in a
 * single HTTP request. Backend does Promise.all internally and caches per-cid.
 *
 * Use from list views: pass ALL visible conditionIds in one call, render each
 * row by doing `map.get(conditionId.toLowerCase())`.
 */
export function useOpenInterestMap(conditionIds: string[], enabled = true) {
  // Stable key: sorted + deduplicated
  const key = Array.from(new Set(conditionIds.map((c) => c.toLowerCase()))).sort().join(",");
  return useQuery<Map<string, number>>({
    queryKey: ["analytics", "oi-bulk", key],
    queryFn: () => fetchOiBulk(conditionIds),
    enabled: enabled && conditionIds.length > 0,
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    retry: false,
  });
}
